import { SUGGESTION_ACTION_LABELS, type FactorySuggestion, type SuggestionAction } from './factory-suggestions';

/**
 * SF-T-304: Ask on a task's suggestion is a live chat with an agent about that one suggestion, as the guard
 * reviews' Ask the AI is (RuleAdvice). The chat runs in the task's worktree, one per suggestion, kept on the
 * suggestion (task_suggestion_chat) so anyone on the task sees it until it's decided. Deciding Yes please, New
 * branch or Backlog carries what was said into what acts on it (suggestionChatTranscript).
 */

/** The decisions a task's suggestions take: Ask is the chat beside them now, not a decision. */
export const TASK_SUGGESTION_ACTIONS = ['yes', 'branch', 'backlog', 'no'] as const satisfies readonly SuggestionAction[];

/** Characters in one question about a suggestion. */
export const SUGGESTION_CHAT_MESSAGE_MAX = 4000;

/** A message in a suggestion's chat, as the task shows it. */
export interface SuggestionChatMessage {
  id: string;
  /** note: the factory's, shown only for a turn that failed (error) and a checkout the turn changed (warn). */
  role: 'user' | 'agent' | 'note';
  /** A note's level. */
  level?: 'error' | 'warn';
  body: string;
  /** A person's: queued (waits for the turn before it) | sent | failed. */
  status: 'queued' | 'sent' | 'failed' | null;
  /** Who asked (a person's message). */
  user_name: string | null;
  created_at: string;
}

/** One suggestion's chat. */
export interface SuggestionChat {
  /** The factory message whose suggestions block holds the suggestion. */
  event_id: string;
  /** The suggestion's number in it (1-based). */
  number: number;
  session_id: string;
  /** idle: waiting for a question; working: the agent is answering; closed: no more questions. */
  status: 'idle' | 'working' | 'closed';
  started_by: string | null;
  /** The agent waits on an approval or its own question, answered only on the chat page. */
  waiting: boolean;
  /** The caller started it, so the chat page (/chat/:id) is theirs to open (and they're an admin). */
  mine: boolean;
  /**
   * The caller can ask in it (with the right to comment): it's theirs, they're an admin, or its starter isn't one. An
   * admin's chat reads every board, so someone who isn't an admin can't ask with its reach. A closed chat: true (a
   * question starts the caller's own).
   */
  can_ask: boolean;
  messages: SuggestionChatMessage[];
}

/** Characters of each part of the suggestion the first message carries. */
const PART_MAX = 2000;
/** Characters of the task's description the first message carries. */
const DESCRIPTION_MAX = 4000;

/**
 * The chat's first message: the task (with its description: a suggestion's chat reads no boards, agent-chat.ts), the suggestion in full, an earlier chat about it that closed, how to answer, then the question. The
 * agent's brief (the chat prompt) already says it changes nothing.
 */
export function suggestionChatBrief({
  taskRef,
  taskTitle,
  taskDescription = '',
  run,
  suggestion,
  number,
  question,
  branch,
  earlier = null,
  kind = 'suggestion',
}: {
  /** SF-T-308: one of the run's questions before it builds, rather than a suggestion at the end. */
  kind?: 'suggestion' | 'question';
  taskRef: string;
  taskTitle: string;
  taskDescription?: string;
  /** The run that suggested it (R-12), when known. */
  run: string | null;
  suggestion: FactorySuggestion;
  number: number;
  question: string;
  /** The branch of the task's worktree, where the chat runs when that machine can take it (null: no run made one). */
  branch: string | null;
  /** What was said in the chats about it before this one (suggestionChatTranscript). */
  earlier?: string | null;
}): string {
  const cut = (s: string | undefined, max = PART_MAX) => (s ?? '').trim().slice(0, max);
  const recommend = suggestion.recommend ? SUGGESTION_ACTION_LABELS[suggestion.recommend] : null;
  const description = cut(taskDescription, DESCRIPTION_MAX);
  const asked = kind === 'question';
  return [
    asked
      ? `I'm answering a question the AI builder asked on ${taskRef} “${taskTitle}”${run ? ` (run ${run})` : ''} before it builds, and I want to talk it over with you first.`
      : `I'm deciding what to do with a suggestion the AI builder made on ${taskRef} “${taskTitle}”${run ? ` (run ${run})` : ''}, and I want to talk it over with you first.`,
    branch
      ? `When the task's worktree (branch \`${branch}\`) is among your checkouts, read what it built there (\`git diff origin/main...HEAD\`); otherwise read its PR.`
      : 'No run has made a worktree for it, so read its PR, if it has one.',
    "The task's description is below. This chat is shown on the task, so it doesn't read the boards (`sf-agent tasks` refuses it): answer from this message and the code.",
    '',
    description ? `## The task\n${description}${taskDescription.trim().length > DESCRIPTION_MAX ? '…' : ''}` : '',
    '',
    `## ${asked ? 'Question' : 'Suggestion'} ${number}: ${suggestion.title}`,
    cut(suggestion.detail),
    suggestion.change ? `**If it's built:** ${cut(suggestion.change)}` : '',
    suggestion.without ? `**Without it:** ${cut(suggestion.without)}` : '',
    recommend ? `**The AI builder recommends:** ${recommend}` : '',
    ...(suggestion.options?.length ? [`**Options:**\n${suggestion.options.map((o) => `- ${o.label}${o.description ? `: ${o.description}` : ''}`).join('\n')}`] : []),
    '',
    earlier ? `## What we said about it before (an earlier chat, now closed)\n${earlier}` : '',
    '',
    '## How to answer',
    asked
      ? 'My choices are one of the options, my own answer, Skip (the AI builder goes with its recommendation) or Backlog (a task for later, left out of this build). I answer on the task, not here.'
      : `My choices are ${SUGGESTION_ACTION_LABELS.yes} or one of its options (the AI builder builds it on this task's branch and PR), ${SUGGESTION_ACTION_LABELS.branch} (a new task stacked on this branch, with its own PR), ${SUGGESTION_ACTION_LABELS.backlog} (a task for later) or ${SUGGESTION_ACTION_LABELS.no}. I pick on the task, not here.`,
    "Answer my question from the code, plainly and briefly. When it helps, end with the choice you would make and why, in a sentence. This chat is shown inside the suggestion, so leave out the blocks your brief describes (factory-changes, factory-questions, factory-suggestions and the rest): nothing there can apply or answer them. Ask me anything you need in plain words instead.",
    '',
    '## My question',
    question.trim(),
  ]
    .filter((line, i, all) => line !== '' || (i > 0 && all[i - 1] !== ''))
    .join('\n');
}

/** Characters of a chat that go with a decision. */
export const SUGGESTION_CHAT_TRANSCRIPT_MAX = 4000;

const LEFT_OUT = '(messages in between left out)';

/**
 * What was said in a suggestion's chat, for the decision that acts on it: each question and answer in turn, and the
 * factory's notes (a checkout the agent changed), `firstQuestion` in place of the long first message (null:
 * `messages` start later than it, so none is the brief). To fit `max` it keeps the first question and answer (often
 * the one that settled it) and the newest, leaving out those in between. A question still unanswered says so. Empty
 * when nobody asked anything.
 */
export function suggestionChatTranscript(
  messages: Array<Pick<SuggestionChatMessage, 'role' | 'body' | 'user_name'>>,
  firstQuestion: string | null,
  max = SUGGESTION_CHAT_TRANSCRIPT_MAX
): string {
  const said = messages.filter((m) => m.role === 'user' || m.role === 'agent' || m.role === 'note');
  if (!said.some((m) => m.role === 'user')) return '';
  const firstUser = firstQuestion === null ? -1 : said.findIndex((m) => m.role === 'user');
  const parts = said.map((m, i) => {
    const body = (i === firstUser && firstQuestion !== null ? firstQuestion : m.body).trim();
    if (m.role === 'note') return `(Factory note: ${body})`;
    return m.role === 'user' ? `Q (${m.user_name ?? 'someone'}): ${body}` : `A: ${body}`;
  });
  const lastSaid = said.filter((m) => m.role !== 'note').at(-1);
  if (lastSaid?.role === 'user') parts.push('(not answered yet when it was decided)');
  const all = parts.join('\n\n');
  if (all.length <= max) return all;

  const clip = (text: string, room: number) => (text.length > room ? `${text.slice(0, Math.max(0, room - 1))}…` : text);
  // The first question and answer, in at most a third of the room.
  const headEnd = parts.length > 1 && parts[1].startsWith('A:') ? 2 : 1;
  const head = clip(parts.slice(0, headEnd).join('\n\n'), Math.floor(max / 3));
  // Then the newest, as many as fit.
  let room = max - head.length - LEFT_OUT.length - 4;
  const tail: string[] = [];
  for (let i = parts.length - 1; i >= headEnd && room > 0; i--) {
    const part = clip(parts[i], room);
    tail.unshift(part);
    room -= part.length + 2;
    if (part !== parts[i]) break;
  }
  const skipped = parts.length - headEnd - tail.length > 0;
  return [head, ...(skipped ? [LEFT_OUT] : []), ...tail].join('\n\n');
}
